import { useMemo, useCallback } from 'react';
import { useTracking } from 'react-tracking';
import parse, {
  DOMNode,
  domToReact,
  Element,
  HTMLReactParserOptions,
  attributesToProps,
} from 'html-react-parser';

import { useCloudflareImage } from '@/hooks/useCloudflareImage';
import { Heading, Text } from '@simplywallst/ui-core';

import { Img, Ol, StyledLink, StyledUniversalLink, Ul } from './styled';

type HeadingType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const EmbeddedImage = (props: { src: string; alt: string }) => {
  const coverImageSrc = useCloudflareImage({ src: props.src });
  return <Img src={coverImageSrc} alt={props.alt} />;
};

const headingTypographyMapping = (heading: HeadingType) => {
  switch (heading) {
    case 'h1':
      return 'heroStrong';
    case 'h2':
      return 'megaStrong';
    case 'h3':
      return 'editorHeading';
    case 'h4':
      return 'editorSubHeading';
    case 'h5':
      return 'baseStrong';
    case 'h6':
      return 'smallStrong';
  }
};

export const useHtmlReactParser = ({ html }: { html: string }) => {
  const { trackEvent } = useTracking();
  const handleClick = useCallback(
    (e) => {
      trackEvent({
        type: 'track',
        action: 'click',
        subject: 'narrative_link',
        href: e.currentTarget.href,
      });
    },
    [trackEvent]
  );
  const parsedHtml = useMemo(() => {
    const parseOptions: HTMLReactParserOptions = {
      replace: (domNode: DOMNode) => {
        if (domNode instanceof Element) {
          switch (domNode.name) {
            case 'ul':
              return <Ul>{domToReact(domNode.children, parseOptions)}</Ul>;
            case 'ol':
              return <Ol>{domToReact(domNode.children, parseOptions)}</Ol>;
            case 'p':
              const parent = domNode.parent as Element;
              const parentName = parent !== null ? parent.name : '';
              return parentName === 'li' ? (
                <>{domToReact(domNode.children, parseOptions)}</>
              ) : (
                <Text as="p" typography="editorBody" color="lightGray">
                  {domToReact(domNode.children, parseOptions)}
                </Text>
              );
            case 'h1':
            case 'h2':
            case 'h3':
            case 'h4':
            case 'h5':
            case 'h6':
              return (
                <Heading
                  color="white"
                  as={domNode.name}
                  typography={headingTypographyMapping(domNode.name)}
                >
                  {domToReact(domNode.children, parseOptions)}
                </Heading>
              );
            case 'b':
            case 'strong':
              return (
                <Text
                  as="strong"
                  typography="editorBodyStrong"
                  color="lightGray"
                >
                  {domToReact(domNode.children, parseOptions)}
                </Text>
              );
            case 'i':
            case 'em':
              return (
                <Text as="em" typography="editorBody" color="lightGray">
                  {domToReact(domNode.children, parseOptions)}
                </Text>
              );
            case 'font':
              return <>{domToReact(domNode.children, parseOptions)}</>;
            case 'figure':
              return (
                <figure>{domToReact(domNode.children, parseOptions)}</figure>
              );
            case 'img': {
              const props = attributesToProps(domNode.attribs);
              return <EmbeddedImage src={props.src} alt={props.alt} />;
            }
          }

          if (domNode.name === 'a' && domNode.attribs.href) {
            if (domNode.attribs.href?.includes('mailto:')) {
              return (
                <StyledLink
                  href={domNode.attribs.href}
                  rel={'nofollow'}
                  onClick={handleClick}
                >
                  {domToReact(domNode.children, parseOptions)}
                </StyledLink>
              );
            }
            return (
              <StyledUniversalLink
                openedNewTab
                to={domNode.attribs.href}
                onClick={handleClick}
              >
                {domToReact(domNode.children, parseOptions)}
              </StyledUniversalLink>
            );
          }
        }
      },
    };

    return parse(html, parseOptions);
  }, [html]);

  return parsedHtml;
};
